import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, json } from "./api";
import type { SaveState } from "./types";
type Versioned = {
  id: string;
  title: string;
  version: number;
  updatedAt: string;
};
export function useResource<T extends Versioned>(
  initial: T,
  path: string,
  onSaved: (data: T) => void,
) {
  const [data, setData] = useState(initial),
    [state, setState] = useState<SaveState>("saved"),
    [message, setMessage] = useState("");
  const current = useRef(initial),
    edit = useRef(0),
    saved = useRef(0),
    conflict = useRef<T | null>(null),
    timer = useRef<ReturnType<typeof setTimeout> | null>(null),
    inflight = useRef<Promise<boolean> | null>(null),
    mounted = useRef(true),
    callback = useRef(onSaved);
  callback.current = onSaved;
  const flush = useCallback(async (): Promise<boolean> => {
    if (timer.current) clearTimeout(timer.current);
    if (conflict.current) return false;
    if (inflight.current && !(await inflight.current)) return false;
    if (edit.current === saved.current) return true;
    const work = (async () => {
      while (edit.current !== saved.current) {
        const revision = edit.current,
          snapshot = current.current;
        if (!snapshot.title.trim()) {
          setState("error");
          setMessage("У материала должно быть название.");
          return false;
        }
        setState("saving");
        try {
          const result = await api<T>(path, json("PUT", snapshot));
          current.current = {
            ...current.current,
            version: result.version,
            updatedAt: result.updatedAt,
          };
          saved.current = revision;
          if (mounted.current) {
            setData({ ...current.current });
            setMessage("");
            setState(edit.current === saved.current ? "saved" : "dirty");
          }
          callback.current(result);
        } catch (e) {
          const err = e as ApiError;
          if (mounted.current) {
            setMessage(err.message);
            conflict.current = (err.data?.current as T) || null;
            setState(conflict.current ? "conflict" : "error");
          }
          return false;
        }
      }
      return true;
    })();
    inflight.current = work;
    try {
      return await work;
    } finally {
      inflight.current = null;
    }
  }, [path]);
  const change = useCallback(
    (patch: Partial<T>) => {
      current.current = { ...current.current, ...patch };
      edit.current++;
      setData({ ...current.current });
      if (!conflict.current) {
        setState("dirty");
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => void flush(), 800);
      }
    },
    [flush],
  );
  useEffect(() => {
    mounted.current = true;
    const unload = (e: BeforeUnloadEvent) => {
      if (edit.current !== saved.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    const online = () => void flush();
    const save = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        void flush();
      }
    };
    window.addEventListener("beforeunload", unload);
    window.addEventListener("online", online);
    window.addEventListener("keydown", save);
    return () => {
      mounted.current = false;
      if (timer.current) clearTimeout(timer.current);
      window.removeEventListener("beforeunload", unload);
      window.removeEventListener("online", online);
      window.removeEventListener("keydown", save);
    };
  }, [flush]);
  function useServer() {
    if (!conflict.current) return;
    current.current = conflict.current;
    conflict.current = null;
    saved.current = edit.current;
    setData(current.current);
    setState("saved");
    setMessage("");
    callback.current(current.current);
  }
  async function keepLocal() {
    if (!conflict.current) return false;
    current.current = { ...current.current, version: conflict.current.version };
    conflict.current = null;
    return flush();
  }
  return {
    data,
    state,
    message,
    change,
    flush,
    useServer,
    keepLocal,
    getCurrent: () => current.current,
  };
}
